import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useTheme } from 'styled-components';
import { ThemeEnum } from '@app/common';
import { ValueDescription } from '../../../value-description/value-description';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { formatDate } from '../../../../../../models/functions/format-date';
import { CardDashboardTradesLoading } from '../card-loading/card-dashboard-trades-loading';
import { IconPortfolio } from '../../../../icon/portfolio/icon-portfolio';
import { TextVariantType } from '../../../../../../theme/type/text-type';
import { ValueAmount } from '../../../../value/amount/value-amount';
import { CardTitle } from '../../../title/card-title';
import { AssetChangeCurrency } from '../../../../forms/assets/asset-change-currency';
import { getAssetTextVariant } from '../../../../../../theme/utils/get-asset-text-variant';
import { normalizePortfolioValue } from '../../../../../../utils/normalize-portfolio-value';

export const CardDashboardTradesPortfolioValue: React.FC = observer(() => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { statisticStore, themeStore } = useRootStore();
    const { overview } = statisticStore.overviewStore;
    const { portfolioStore } = useRootStore();

    const cardTextColor = getAssetTextVariant(
        portfolioStore.selectedPortfolio.colorName,
        themeStore.theme as ThemeEnum,
    ) as TextVariantType;

    const backgroundColor = portfolioStore.selectedPortfolio.colorName
        ? theme.assetColor[portfolioStore.selectedPortfolio.colorName].main
        : theme.assetColor.blue.main;

    return (
        <CardDashboardTradesLoading fields={['portfolioValue', 'lastUpdated']}>
            <div
                className="relative flex h-full flex-col justify-between rounded-large p-[1.6rem] shadow-strong lg:p-[2.2rem]"
                style={{ backgroundColor }}
            >
                <div className="relative inset-0 flex flex-col items-start">
                    <CardTitle
                        title={t('layout.statisticsCard.portfolioValue.title')}
                        tooltip={t('layout.statisticsCard.portfolioValue.tooltip')}
                        color={cardTextColor}
                    />
                    <div
                        className="flex items-center gap-vsmall text-vlarge leading-vlarge font-bold"
                        style={{ color: cardTextColor }}
                    >
                        <IconPortfolio
                            type={portfolioStore.selectedPortfolio.iconName}
                            variant={portfolioStore.selectedPortfolio.colorName}
                            size={'vsmall'}
                            dark
                        />
                        {portfolioStore.selectedPortfolio.name}
                    </div>
                </div>

                {overview && (
                    <ValueDescription
                        value={
                            <div className="flex items-center gap-xsmall">
                                <ValueAmount
                                    amount={
                                        normalizePortfolioValue(overview.portfolioValue) +
                                        statisticStore.ratiosStore.unrealizedTodayProfit.totalTodayProfit
                                    }
                                    currency={portfolioStore.selectedPortfolio.portfolioHistoryCurrencyCode}
                                    mode="pure"
                                    hidable
                                />
                                <AssetChangeCurrency />
                            </div>
                        }
                        description={t('layout.statisticsCard.portfolioValue.recalculatedOn', {
                            value: formatDate(overview.lastUpdated, true),
                        })}
                        padding={'none'}
                        valueColor={cardTextColor}
                        descriptionColor={cardTextColor}
                    />
                )}
            </div>
        </CardDashboardTradesLoading>
    );
});
